import type { ClientOfferTerms } from "@hakili/contracts";
import { formatDate, formatMoney, t } from "@hakili/i18n";
import { Button, DEALER_WARN_NOTICE, LIEN_TEXTE } from "@hakili/ui";
import { AlertTriangle, Check, FileText, Info, Lock, MessageCircle, Phone } from "lucide-react";
import { type ReactElement, useCallback, useEffect, useState } from "react";

import type { DealerDocumentsAnswer } from "@hakili/contracts";
import type { DealerDossierDetail, DealerDossierDetailRead } from "../session/dealer-dossier-detail.js";
import type { DoorResult } from "../session/useSession.js";
import type { IssuedProforma } from "../session/vehicle-papers.js";
import { DocumentsStepOfDossier } from "./documents-step.js";
import { dayOf, sinceWords } from "./dossier-dates.js";
import { IssuedNotice, issuedLineOf, RefusalNotice } from "./proforma-outcome.js";
import { ReadFailure } from "./read-failure.js";
import { ReceiptDialog } from "./receipt-dialog.js";

/**
 * `P1-DLW-052` (DEALER V2 B5-b, frames `f8714054`, sources `e647efb2`; the architect's rulings of 2 November 2026): **one dossier of the dealership, read top to bottom.**
 *
 * The header (the client, the car, the phone, the origin as served), « Appeler » and « WhatsApp » which OPEN THE DEALER'S OWN PHONE APP (a `tel:` and a `wa.me` link: no server send); the parcours as served and « depuis N jours » from the SERVER's `asOf` (OD-17); the served status and its ONE action, the list's own (« Émettre la facture » says its result in the Accueil pattern, « Confirmer la réception » opens the receipt dialog, one key per opening); the offer card with EXACTLY the four served terms (`offer.terms`, `#906`'s shape) and never `offer.amountVehicle`; « Ce que vous recevrez »; the administrator's facts, DRAWN when the door served the key and NOT DRAWN when it is absent (never an empty section); Hakili's strip (its own card above the pièces, only while the page carries no money block and the dossier is open) and lane F's pièces step (`DocumentsStep`, read-only here: no « continuer » footer, no deposit).
 *
 * Every figure and every label is served: the offer's four labels (`offer.terms.labels`), the receipt's three (`receipt.labels`), the receipt's sentence, the facts' text. Nothing is counted or summed here. NOT drawn (the contract serves none): « Accord du client » and « Le journal » of the frames.
 */
export interface DossierDetailProps {
  readonly dossierId: string;
  readonly readDetail: (dossierId: string) => Promise<DealerDossierDetailRead>;
  readonly readDocuments: (dossierId: string) => Promise<DoorResult<DealerDocumentsAnswer>>;
  readonly issueProforma: (input: {
    vehicleId: string;
    dossierId: string;
  }) => Promise<DoorResult<IssuedProforma>>;
  readonly confirmReceipt: (
    settlementId: string,
    receivedOn: string,
    idempotencyKey?: string,
  ) => Promise<DoorResult<unknown>>;
  readonly naviguer: (path: string) => void;
}

const fill = (key: string, values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.split(`{${name}}`).join(value), t(key));

const NBSP = " ";
const CARD = "rounded-2xl border border-gray-200 bg-white px-6 py-5";
const WAITING_PILL: Readonly<Record<string, string>> = {
  dealer: "bg-amber-50 text-amber-700",
  bank: "bg-indigo-50 text-indigo-700",
};

const initialsOf = (name: string): string => {
  const words = name.trim().split(/\s+/u).filter(Boolean);
  return ((words[0]?.at(0) ?? "") + (words.length > 1 ? (words.at(-1)?.at(0) ?? "") : "")).toUpperCase();
};

function Parcours(props: { readonly detail: DealerDossierDetail; readonly today: string }): ReactElement {
  const { steps, currentStep } = props.detail.dossier.parcours;
  const since = sinceWords(props.detail.dossier.since, props.today);
  // A closed dossier (`status.waitingOn === null`, the contract's own definition) has NO current step: the steps done stay done, the rest stay ahead (R's frames 44 and 75).
  const closed = props.detail.dossier.status.waitingOn === null;
  return (
    <ol
      className="m-0 mt-5 grid list-none gap-3 p-0"
      style={{ gridTemplateColumns: `repeat(${String(steps.length)}, minmax(0, 1fr))` }}
      aria-label={t("dealer.dossier.parcours")}
    >
      {steps.map((name, index) => {
        const state =
          currentStep === null
            ? "avenir"
            : index < currentStep
              ? "faite"
              : index === currentStep && !closed
                ? "courante"
                : "avenir";
        return (
          <li
            key={name}
            data-testid="dealer-dossier-etape"
            data-nom={name}
            data-state={state}
            aria-current={state === "courante" ? "step" : undefined}
          >
            <span
              className={`flex h-7 w-7 items-center justify-center rounded-full border-2 text-xs font-semibold ${
                state === "faite"
                  ? "border-emerald-700 bg-emerald-700 text-white"
                  : state === "courante"
                    ? "border-indigo-600 bg-indigo-600 text-white"
                    : "border-gray-300 bg-white text-gray-500"
              }`}
            >
              {state === "faite" ? <Check className="h-4 w-4" aria-hidden="true" /> : String(index + 1)}
            </span>
            <span className="mt-1 block text-sm font-semibold text-gray-900">{name}</span>
            {state === "courante" && since !== null ? (
              <span className="block text-xs text-gray-600" data-testid="dealer-dossier-depuis">
                {since}
              </span>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

const ratePct = (rate: number): string => `${String(rate).replace(".", ",")}${NBSP}%`;

function OfferCard(props: { readonly bankName: string; readonly terms: ClientOfferTerms }): ReactElement {
  const { terms } = props;
  const rows: readonly (readonly [string, string])[] = [
    [terms.labels.amountFinanced, formatMoney(terms.amountFinancedAmount)],
    [terms.labels.duration, fill("dealer.dossier.dureeMois", { n: String(terms.durationMonths) })],
    [terms.labels.nominalRate, ratePct(terms.nominalRatePct)],
    [terms.labels.instalment, formatMoney(terms.instalmentAmount)],
  ];
  return (
    <section data-testid="dealer-dossier-offre" className={CARD}>
      <h2 className="m-0 text-base font-semibold text-gray-900">{t("dealer.dossier.offreTitre")}</h2>
      <p className="m-0 mt-0.5 text-sm text-gray-600" data-testid="dealer-dossier-offre-banque">
        {props.bankName}
      </p>
      <dl className="m-0 mt-4 grid gap-3 sm:grid-cols-2">
        {rows.map(([label, value]) => (
          <div
            key={label}
            data-testid="dealer-dossier-offre-terme"
            data-terme={label}
            className="rounded-xl bg-gray-50 px-4 py-3"
          >
            <dt className="text-xs text-gray-600">{label}</dt>
            <dd className="m-0 text-lg font-semibold tabular-nums text-gray-900">{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

type Receipt = NonNullable<DealerDossierDetail["receipt"]>;

/** « Quand » is the day the dealer stated he received the money (A50): drawn only once it is served, nothing stands in for it before. */
const whenOf = (receipt: Receipt): string | null =>
  receipt.receivedOn === null
    ? null
    : fill("dealer.dossier.quandRecu", { date: formatDate(new Date(`${receipt.receivedOn}T00:00:00.000Z`)) });

function ReceiptCard(props: { readonly receipt: Receipt }): ReactElement {
  const { receipt } = props;
  const when = whenOf(receipt);
  const rows: readonly (readonly [string, string, string])[] = [
    [receipt.labels.share, formatMoney(receipt.dealerAmount), "dealer-dossier-recu-part"],
    [
      receipt.labels.payer,
      receipt.payerKind === "acheteur"
        ? t("dealer.dossier.payeurAcheteur")
        : t("dealer.dossier.payeurBanque"),
      "dealer-dossier-recu-payeur",
    ],
    ...(when === null ? [] : ([[receipt.labels.when, when, "dealer-dossier-recu-quand"]] as const)),
  ];
  return (
    <section data-testid="dealer-dossier-recu" className={CARD}>
      <h2 className="m-0 text-base font-semibold text-gray-900">{t("dealer.dossier.recuTitre")}</h2>
      <dl className="m-0 mt-3 grid gap-2">
        {rows.map(([label, value, testId]) => (
          <div
            key={testId}
            className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-100 py-2 last:border-b-0"
          >
            <dt className="text-sm text-gray-600">{label}</dt>
            <dd className="m-0 text-base font-semibold text-gray-900" data-testid={testId}>
              {value}
            </dd>
          </div>
        ))}
      </dl>
      <p className="m-0 mt-3 text-sm text-gray-600" data-testid="dealer-dossier-recu-phrase">
        {receipt.sentence}
      </p>
    </section>
  );
}

export function DossierDetail(props: DossierDetailProps): ReactElement {
  const { dossierId, readDetail, issueProforma, confirmReceipt } = props;
  const [read, setRead] = useState<DealerDossierDetailRead | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ readonly who: string; readonly line: string } | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [receipt, setReceipt] = useState<{ readonly settlementId: string; readonly key: string } | null>(
    null,
  );

  const load = useCallback(async (): Promise<void> => {
    setRead(await readDetail(dossierId));
  }, [readDetail, dossierId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (read === null) return <div data-testid="dealer-dossier-chargement" aria-busy="true" />;

  if (!read.ok) {
    return (
      <section>
        <button
          type="button"
          className={LIEN_TEXTE}
          data-testid="dealer-dossier-retour"
          onClick={() => {
            props.naviguer("/dossiers");
          }}
        >
          {t("dealer.dossiers.titre")}
        </button>
        <div className="mt-4">
          <ReadFailure
            what="dealer.lecture.dossier"
            message={read.message}
            onRetry={() => {
              setRead(null);
              void load();
            }}
            testId="dealer-dossier-echec"
            retryTestId="dealer-dossier-echec-reessayer"
          />
        </div>
      </section>
    );
  }

  const detail = read.value;
  const { dossier } = detail;
  const today = dayOf(detail.asOf);
  const digits = dossier.client.phone.replace(/[^\d]/gu, "");
  const action = dossier.status.action;
  // The dealer's turn is what the server SERVES: `waitingOn: "dealer"` and its `waitingText` (`STATUS-OF-FIXES`, #939: at `decaisse` the concession's wait is `dealer` for every reader; the ACTION is the right-holder's alone).
  const dealersTurn = dossier.status.waitingOn === "dealer";

  const act = async (): Promise<void> => {
    if (action === null || issuing) return;
    setRowError(null);
    setIssued(null);
    if (action.code === "confirmerReception") {
      setReceipt({ settlementId: action.target.settlementId, key: globalThis.crypto.randomUUID() });
      return;
    }
    setIssuing(true);
    const done = await issueProforma({
      vehicleId: action.target.vehicleId,
      dossierId: action.target.dossierId,
    });
    setIssuing(false);
    if (!done.ok) {
      setRowError(done.message);
      return;
    }
    setIssued({ who: `${dossier.client.name} · ${dossier.vehicle.label}`, line: issuedLineOf(done.value) });
    await load();
  };

  const car = [dossier.vehicle.label, dossier.vehicle.stockReference, dossier.client.phone]
    .filter((part): part is string => part !== null && part !== "")
    .join(" · ");

  return (
    <section>
      <nav aria-label="Fil d'Ariane" className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <button
          type="button"
          className={LIEN_TEXTE}
          data-testid="dealer-dossier-retour"
          onClick={() => {
            props.naviguer("/dossiers");
          }}
        >
          {t("dealer.dossiers.titre")}
        </button>
        <span aria-hidden="true">›</span>
        <span className="font-semibold text-gray-900">{`${dossier.client.name} · ${dossier.vehicle.label}`}</span>
      </nav>
      <header className={`${CARD} mt-3`} data-testid="dealer-dossier-entete">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <span
              aria-hidden="true"
              className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-900 text-lg font-semibold text-white"
            >
              {initialsOf(dossier.client.name)}
            </span>
            <div>
              <h1 className="m-0 text-2xl font-semibold text-gray-900" data-testid="dealer-dossier-client">
                {dossier.client.name}
              </h1>
              <p className="m-0 text-sm text-gray-600" data-testid="dealer-dossier-voiture">
                {car}
              </p>
              <span
                className="mt-1 inline-flex rounded-full bg-indigo-50 px-3 py-0.5 text-xs font-semibold text-indigo-700"
                data-testid="dealer-dossier-origine"
              >
                {dossier.origin.words}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <a
              href={`tel:+${digits}`}
              data-testid="dealer-dossier-appeler"
              className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-900"
            >
              <Phone className="h-4 w-4" aria-hidden="true" />
              {t("dealer.dossier.appeler")}
            </a>
            <a
              href={`https://wa.me/${digits}`}
              data-testid="dealer-dossier-whatsapp"
              className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-900"
            >
              <MessageCircle className="h-4 w-4" aria-hidden="true" />
              {t("dealer.dossier.whatsapp")}
            </a>
          </div>
        </div>

        <Parcours detail={detail} today={today} />

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-gray-50 px-4 py-3">
          <p className="m-0 flex flex-wrap items-center gap-3 text-sm text-gray-900">
            {dossier.status.waitingText === null ? null : (
              <span
                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${WAITING_PILL[dossier.status.waitingOn ?? ""] ?? "bg-gray-50 text-gray-700"}`}
                data-testid="dealer-dossier-attente"
              >
                {dossier.status.waitingText}
              </span>
            )}
            {dossier.status.nextText === null ? null : (
              <span data-testid="dealer-dossier-suite">{dossier.status.nextText}</span>
            )}
          </p>
          {action === null ? null : (
            <Button
              variant={dealersTurn ? "primary" : "secondary"}
              size="sm"
              {...(dealersTurn ? { "data-awaits": "dealer" } : {})}
              disabled={issuing}
              onClick={() => {
                void act();
              }}
              data-testid="dealer-dossier-action"
            >
              {action.code === "emettreProforma" ? (
                <FileText className="mr-1.5 inline h-4 w-4" aria-hidden="true" />
              ) : (
                <Check className="mr-1.5 inline h-4 w-4" aria-hidden="true" />
              )}
              {action.label}
            </Button>
          )}
        </div>
        {rowError === null ? null : <RefusalNotice message={rowError} testId="dealer-dossier-erreur" />}
        {issued === null ? null : (
          <IssuedNotice who={issued.who} line={issued.line} testId="dealer-dossier-proforma-emise" />
        )}
      </header>

      {detail.closure === undefined || detail.closure === null ? null : (
        // `DEALER-CLOSURE-FIELD` (#937): the SERVED sentence of a dossier closed « sans suite », one of three, never composed here; the closure's code and the staff's motive are never served.
        <div
          role="status"
          data-testid="dealer-dossier-clos"
          className="mt-4 flex items-start gap-2 rounded-2xl border border-gray-200 bg-white px-5 py-4 text-sm text-gray-900"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-gray-600" aria-hidden="true" />
          <span>{detail.closure.words}</span>
        </div>
      )}

      {dossier.status.waitingOn === null || detail.holdSentence === null ? null : (
        <div
          role="status"
          data-testid="dealer-dossier-alerte"
          className={`mt-4 flex items-start gap-2 rounded-2xl border ${DEALER_WARN_NOTICE} px-5 py-4 text-sm text-gray-900`}
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
          <span>{detail.holdSentence}</span>
        </div>
      )}

      <div
        className={`mt-4 grid items-start gap-4 ${detail.accessFacts === undefined ? "" : "lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]"}`}
      >
        <div className="grid gap-4">
          {detail.offer === null ? null : (
            <OfferCard bankName={detail.offer.bankName} terms={detail.offer.terms} />
          )}
          {detail.receipt === null ? null : <ReceiptCard receipt={detail.receipt} />}
          <DocumentsStepOfDossier
            dossierId={dossierId}
            readDocuments={props.readDocuments}
            withStrip={detail.offer === null && detail.receipt === null && dossier.status.waitingOn !== null}
          />
        </div>
        {detail.accessFacts === undefined ? null : (
          <section data-testid="dealer-dossier-acces" className={CARD}>
            <h2 className="m-0 text-base font-semibold text-gray-900">{t("dealer.dossier.accesTitre")}</h2>
            {detail.accessFacts.length === 0 ? (
              <p className="m-0 mt-2 text-sm text-gray-600">{t("dealer.dossier.accesVide")}</p>
            ) : (
              <>
                <ul className="m-0 mt-2 list-none p-0">
                  {detail.accessFacts.map((fact) => (
                    <li
                      key={fact.id}
                      className="flex items-start gap-3 border-b border-gray-100 py-2 text-sm font-semibold text-gray-900 last:border-b-0"
                      data-testid="dealer-dossier-acces-fait"
                    >
                      <Lock
                        className="mt-0.5 h-4 w-4 shrink-0 text-gray-600"
                        aria-hidden="true"
                        data-icone="cadenas"
                      />
                      <span data-fait-texte>{fact.text}</span>
                    </li>
                  ))}
                </ul>
                <p className="m-0 mt-2 text-xs text-gray-600" data-testid="dealer-dossier-acces-explication">
                  {t("dealer.dossier.accesExplication")}
                </p>
              </>
            )}
          </section>
        )}
      </div>

      {receipt === null ? null : (
        <ReceiptDialog
          today={today}
          onCancel={() => {
            setReceipt(null);
          }}
          onConfirm={(receivedOn) => confirmReceipt(receipt.settlementId, receivedOn, receipt.key)}
          onDone={() => {
            setReceipt(null);
            void load();
          }}
        />
      )}
    </section>
  );
}
